import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import {
  createEventResponsibility,
  deleteEventResponsibility,
  getEventResponsibilities,
  updateEventResponsibility,
} from '../api/event-responsibilityAPI';

const sortResponsibilities = (items) =>
  [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));

const EventResponsibilityLayer = () => {
  const userProfile = useUserProfile();
  const [responsibilities, setResponsibilities] = useState([]);
  const [formState, setFormState] = useState({ name: '', default: 0 });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const validationFields = [{ field: 'name', label: 'Name', required: true }];
  const { errors, setErrors, validate } = useValidator(validationFields, formState);

  const resetDataTable = (fullyDestroy = false) => {
    if (dtRef.current) {
      dtRef.current.destroy(fullyDestroy);
      dtRef.current = null;
    }
  };

  const fetchResponsibilities = async () => {
    try {
      const data = await getEventResponsibilities();
      setResponsibilities(sortResponsibilities(data || []));
    } catch (error) {
      console.error('Error loading event responsibilities', error);
    }
  };

  useEffect(() => {
    fetchResponsibilities();
  }, []);

  useEffect(() => {
    resetDataTable();

    if (responsibilities.length) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => resetDataTable(true);
  }, [responsibilities]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const resetForm = () => {
    setFormState({ name: '', default: 0 });
    setEditingId(null);
    setErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleEdit = (responsibility) => {
    setFormState({
      name: responsibility.name || '',
      default: responsibility.default === 1 || responsibility.default === true ? 1 : 0,
    });
    setEditingId(responsibility.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name: formState.name?.trim(),
      default: formState.default === 1 || formState.default === true ? 1 : 0,
    };
    const shouldSetDefault = payload.default === 1;

    try {
      const saved = editingId
        ? await updateEventResponsibility(editingId, payload)
        : await createEventResponsibility(payload);

      setShowModal(false);
      resetForm();
      resetDataTable();

      if (saved) {
        setResponsibilities((prev) => {
          const applyDefaultReset = (items) =>
            shouldSetDefault
              ? items.map((item) => (item.id === saved.id ? item : { ...item, default: 0 }))
              : items;
          if (editingId) {
            const updated = prev.map((item) => (item.id === saved.id ? saved : item));
            return sortResponsibilities(applyDefaultReset(updated));
          }
          return sortResponsibilities(applyDefaultReset([...prev, saved]));
        });
      } else {
        fetchResponsibilities();
      }
    } catch (error) {
      console.error('Error saving event responsibility', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event responsibility?')) return;

    try {
      await deleteEventResponsibility(id);
      resetDataTable();
      setResponsibilities((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error deleting event responsibility', error);
    }
  };

  const canCreate = userProfile?.privileges?.includes('event-responsibility:create');
  const canUpdate = userProfile?.privileges?.includes('event-responsibility:update');
  const canDelete = userProfile?.privileges?.includes('event-responsibility:delete');

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {canCreate && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Event Responsibility
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm'>
          <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {responsibilities.length === 0 && (
                <tr>
                  <td colSpan='3' className='text-center py-4 rounded-0'>No Event Responsibilities Setup</td>
                </tr>
              )}
              {responsibilities.map((responsibility) => (
                <tr key={responsibility.id}>
                  <td><strong>{responsibility.name}</strong></td>
                  <td>
                    <div className='form-switch switch-primary d-flex align-items-center gap-3 mb-0'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        role='switch'
                        id={`default-event-responsibility-${responsibility.id}`}
                        checked={responsibility.default === 1 || responsibility.default === true}
                        disabled
                      />
                      <label
                        className='form-check-label'
                        htmlFor={`default-event-responsibility-${responsibility.id}`}
                      >
                        {responsibility.default === 1 || responsibility.default === true ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className='d-flex gap-2'>
                      {canUpdate && (
                        <button
                          className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleEdit(responsibility)}
                        >
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleDelete(responsibility.id)}
                        >
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  {editingId ? 'Edit Event Responsibility' : 'New Event Responsibility'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Name *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name='name'
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label d-block'>Default</label>
                  <div className='form-switch switch-primary d-flex align-items-center gap-3 mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      role='switch'
                      name='default'
                      checked={formState.default === 1 || formState.default === true}
                      onChange={handleInputChange}
                    />
                    <span className='form-check-label'>
                      {formState.default === 1 || formState.default === true ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Close
                </button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventResponsibilityLayer;
